import { useEffect, useState } from 'react';
import { Button, ConfigProvider, Modal, Spin, Tabs, Tag, message, theme as antdTheme } from 'antd';
import {
  CloudServerOutlined,
  DatabaseOutlined,
  DownOutlined,
  GiftOutlined,
  KeyOutlined,
  LockOutlined,
  MailOutlined,
  RightOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  treasureGetDatabase,
  treasureGetServer,
  treasureSendDatabaseCredentials,
  treasureSendServerCredentials,
  type DatabaseGrant,
  type ServerGrant,
} from '@/services/treasure';
import PasswordsTab from '@/components/PasswordsTab';
import ServiceSecretsTab from '@/components/ServiceSecretsTab';
import styles from './Treasure.module.css';

function Treasure() {
  const navigate = useNavigate();
  const [dbGrants, setDbGrants] = useState<DatabaseGrant[]>([]);
  const [serverGrants, setServerGrants] = useState<ServerGrant[]>([]);
  const [dbLoading, setDbLoading] = useState(true);
  const [serverLoading, setServerLoading] = useState(true);
  const [dbSending, setDbSending] = useState(false);
  const [serverSending, setServerSending] = useState(false);
  const [expandedDbId, setExpandedDbId] = useState<string | null>(null);

  useEffect(() => {
    treasureGetDatabase()
      .then(setDbGrants)
      .catch(() => setDbGrants([]))
      .finally(() => setDbLoading(false));
    treasureGetServer()
      .then(setServerGrants)
      .catch(() => setServerGrants([]))
      .finally(() => setServerLoading(false));
  }, []);

  const handleSend = (type: 'database' | 'server') => {
    const isDb = type === 'database';
    Modal.confirm({
      title: isDb ? '发送数据库凭据' : '发送服务器凭据',
      content: isDb
        ? '系统将向您的注册邮箱发送数据库连接信息（含密码）邮件。确认发送？'
        : '系统将向您的注册邮箱发送服务器 SSH 连接信息（含密码）邮件。确认发送？',
      okText: '确认发送',
      cancelText: '取消',
      onOk: async () => {
        isDb ? setDbSending(true) : setServerSending(true);
        try {
          const r = isDb
            ? await treasureSendDatabaseCredentials()
            : await treasureSendServerCredentials();
          if (r.sent) {
            message.success(`凭据已发送至 ${r.email}`);
          } else {
            message.warning(`发送失败：${r.error || '平台未配置邮件服务'}`);
          }
        } catch {
          message.error('发送失败，请稍后再试');
        } finally {
          isDb ? setDbSending(false) : setServerSending(false);
        }
      },
    });
  };

  const dbContent = (
    <div>
      {dbLoading ? (
        <div className={styles.loadingArea}><Spin /></div>
      ) : dbGrants.length === 0 ? (
        <div className={styles.emptyArea}>暂无数据库权限</div>
      ) : (
        <div className={styles.grantList}>
          {dbGrants.map((g) => {
            const isExpanded = expandedDbId === g.grant_id;
            return (
              <div key={g.grant_id} className={styles.grantCard}>
                <div
                  className={g.tables.length > 0 ? styles.grantCardHeader : undefined}
                  onClick={g.tables.length > 0 ? () => setExpandedDbId(isExpanded ? null : g.grant_id) : undefined}
                >
                  <div className={styles.grantHost}>
                    <DatabaseOutlined style={{ marginRight: 8 }} />
                    {g.database.name || `${g.database.host}:${g.database.port}${g.database.db_name ? '/' + g.database.db_name : ''}`}
                  </div>
                  {g.tables.length > 0 && (
                    <div className={styles.grantCardRight}>
                      {!isExpanded && (
                        <span className={styles.tableCount}>{g.tables.length} 张表</span>
                      )}
                      {isExpanded
                        ? <DownOutlined className={styles.expandIcon} />
                        : <RightOutlined className={styles.expandIcon} />
                      }
                    </div>
                  )}
                </div>
                <div className={styles.grantMeta}>
                  <span className={styles.grantLabel}>连接地址</span>
                  <code className={styles.grantValue}>{g.database.host}:{g.database.port}{g.database.db_name ? '/' + g.database.db_name : ''}</code>
                </div>
                <div className={styles.grantMeta}>
                  <span className={styles.grantLabel}>用户名</span>
                  <code className={styles.grantValue}>{g.database.username}</code>
                </div>
                {isExpanded && g.tables.length > 0 && (
                  <div className={styles.grantTables}>
                    <span className={styles.grantLabel}>授权表</span>
                    <div className={styles.tagList}>
                      {g.tables.map((t) => (
                        <Tag
                          key={t.table_name}
                          color="rgba(0,255,255,0.1)"
                          style={{ color: 'rgba(0,255,255,0.75)', border: 'none', fontSize: 12 }}
                        >
                          {t.table_name}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className={styles.sendArea}>
        <Button
          icon={<MailOutlined />}
          loading={dbSending}
          className={styles.sendBtn}
          onClick={() => handleSend('database')}
        >
          发送数据库凭据到邮箱
        </Button>
      </div>
    </div>
  );

  const serverContent = (
    <div>
      {serverLoading ? (
        <div className={styles.loadingArea}><Spin /></div>
      ) : serverGrants.length === 0 ? (
        <div className={styles.emptyArea}>暂无服务器权限</div>
      ) : (
        <div className={styles.grantList}>
          {serverGrants.map((g) => (
            <div key={g.grant_id} className={styles.grantCard}>
              <div className={styles.grantHost}>
                <CloudServerOutlined style={{ marginRight: 8 }} />
                {g.server.name || `${g.server.host}:${g.server.port}`}
              </div>
              <div className={styles.grantMeta}>
                <span className={styles.grantLabel}>连接地址</span>
                <code className={styles.grantValue}>{g.server.host}:{g.server.port}</code>
              </div>
              <div className={styles.grantMeta}>
                <span className={styles.grantLabel}>SSH 用户名</span>
                <code className={styles.grantValue}>{g.server.ssh_username}</code>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className={styles.sendArea}>
        <Button
          icon={<MailOutlined />}
          loading={serverSending}
          className={styles.sendBtn}
          onClick={() => handleSend('server')}
        >
          发送服务器凭据到邮箱
        </Button>
      </div>
    </div>
  );

  return (
    <ConfigProvider
      theme={{
        algorithm: antdTheme.darkAlgorithm,
        token: {
          colorPrimary: '#00ffff',
          colorInfo: '#00ffff',
          colorBgContainer: 'rgba(10, 22, 44, 0.85)',
          colorBgElevated: 'rgba(10, 22, 44, 0.95)',
          colorBorder: 'rgba(0, 255, 255, 0.18)',
          colorBorderSecondary: 'rgba(0, 255, 255, 0.12)',
          fontFamily: "'PingFang SC', 'Microsoft YaHei', sans-serif",
          borderRadius: 2,
        },
      }}
    >
      <div className={styles.page}>
        <div className={styles.gridBackground} />
        <span className={`${styles.hudCorner} ${styles.tl}`} />
        <span className={`${styles.hudCorner} ${styles.tr}`} />
        <span className={`${styles.hudCorner} ${styles.bl}`} />
        <span className={`${styles.hudCorner} ${styles.br}`} />

        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.titleGroup}>
              <span className={styles.crumb}>AdenCockpit / MY TREASURE</span>
              <span className={styles.title}>
                <GiftOutlined className={styles.titleIcon} />
                我的宝箱
              </span>
              <span className={styles.subtitle}>DATA · ACCESS · VAULT</span>
            </div>
            <Button
              icon={<RollbackOutlined />}
              className={styles.backBtn}
              onClick={() => navigate('/dashboard')}
            >
              返回驾驶舱
            </Button>
          </div>

          <Tabs
            items={[
              {
                key: 'database',
                label: <span><DatabaseOutlined /> 数据库权限</span>,
                children: dbContent,
              },
              {
                key: 'server',
                label: <span><CloudServerOutlined /> 服务器权限</span>,
                children: serverContent,
              },
              {
                key: 'passwords',
                label: <span><LockOutlined /> 私人密码</span>,
                children: <PasswordsTab />,
              },
              {
                key: 'service-secrets',
                label: <span><KeyOutlined /> 服务密钥</span>,
                children: <ServiceSecretsTab />,
              },
            ]}
          />
        </div>
      </div>
    </ConfigProvider>
  );
}

export default Treasure;
